import {
  BrowserTracing,
  init,
  Replay,
  vueRouterInstrumentation,
} from '@sentry/vue'
import router from '@/router'
import { CaptureConsole } from '@sentry/integrations'
import { App } from 'vue'

export const initSentry = (app: App<Element>, envsToExclude: string[]) => {
  if (envsToExclude.includes(import.meta.env.MODE)) {
    console.info('Sentry is disabled for this environment')
    return
  }
  init({
    app,
    environment: import.meta.env.MODE,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tracePropagationTargets: [
      'https://staging-wl-engine.bionicwp.com',
      'https://bionic-prod-engine.bionicwp.com',
    ],
    integrations: [
      new BrowserTracing({
        routingInstrumentation: vueRouterInstrumentation(router),
      }),
      new Replay({
        mask: ['password'], // This will mask the password field in the session replay
        blockAllMedia: true,
        // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        sessionSampleRate: import.meta.env.MODE === 'development' ? 1.0 : 0.4,
        networkCaptureBodies: true,
        networkDetailAllowUrls: [
          'https://staging-wl-engine.bionicwp.com',
          'https://bionic-prod-engine.bionicwp.com',
        ],
      }),
      new CaptureConsole({
        levels: ['error'],
      }),
    ],
    logErrors: true,
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'development' ? 1.0 : 0.2, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate:
      import.meta.env.MODE === 'development' ? 1.0 : 0.2, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // Filter out third-party errors before sending to Sentry
    beforeSend(event, hint) {
      // Filter out console.error(true) from third-party libraries
      if (event.logger === 'console' && event.message === 'true') {
        return null
      }

      // Filter out LogRocket errors
      if (event.message && event.message.includes('LogRocket')) {
        return null
      }

      // Filter out Tawk.to errors
      if (event.message && event.message.includes('tawk')) {
        return null
      }

      // Filter out Axios URLSearchParams errors from old browsers
      if (
        event.exception?.values?.some(
          (v) =>
            v.type === 'TypeError' &&
            v.value?.includes('Cannot assign to read only property') &&
            v.value?.includes('toString'),
        )
      ) {
        return null
      }

      // Filter out Vue internal errors
      if (
        event.message &&
        (event.message.includes(
          "Cannot read properties of undefined (reading 'length')",
        ) ||
          event.message.includes(
            "Cannot read properties of null (reading 'nextSibling')",
          ) ||
          event.message.includes(
            "Cannot read properties of undefined (reading 'nextSibling')",
          ))
      ) {
        return null
      }

      // Filter out CSS preload errors - these are non-critical
      // If CSS preload fails, the browser will still load CSS normally
      if (
        event.message &&
        (event.message.includes('Unable to preload CSS') ||
          event.message.includes('preload CSS for'))
      ) {
        return null
      }

      // Filter out CSS-related unhandled rejections from resource preloading
      // Check tags for mechanism type and exception values for CSS/preload errors
      const isUnhandledRejection = event.tags?.mechanism === 'onunhandledrejection'
      if (
        isUnhandledRejection &&
        event.exception?.values?.some(
          (v) =>
            v.value?.includes('CSS') ||
            v.value?.includes('preload') ||
            v.type?.includes('CSS'),
        )
      ) {
        return null
      }

      return event
    },
  })
}
